import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { Alert, Card, Checkbox, Select, Spinner } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/errors';
import { type CanchaBoard, type CanchaRow, formatDay, SIDES, sideName, sideStyle } from './data';
import { ProjectsNav } from './ProjectsNav';

export const useCancha = (filters: { organizacion?: string | null; proyecto?: number | null; estancados?: boolean }) => {
  const query = new URLSearchParams();
  if (filters.organizacion) query.set('organizacion', filters.organizacion);
  if (filters.proyecto) query.set('proyecto', String(filters.proyecto));
  if (filters.estancados) query.set('estancados', 'true');
  return useQuery({ queryKey: ['proyectos', 'cancha', filters], queryFn: () => api<CanchaBoard>(`/api/proyectos/cancha?${query}`) });
};

/** Etiqueta del lado de la pelota. */
export function SideBadge({ side }: { side: CanchaRow['side'] }) {
  return <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${sideStyle(side)}`}>{sideName(side)}</span>;
}

/**
 * Tablero Cancha (guía 7.2): en cada proyecto activo, la tarea que tiene la pelota y de qué lado está (IT o negocio),
 * con alerta si no hay movimiento en los días hábiles configurados.
 */
export function CanchaPage() {
  const [params, setParams] = useSearchParams();
  const organization = params.get('organizacion');
  const staleOnly = params.get('estancados') === 'si';
  const board = useCancha({ organizacion: organization, estancados: staleOnly });
  // Las organizaciones del filtro salen del propio tablero (las que el usuario ve).
  const all = useCancha({});
  const organizations = [...new Map((all.data?.projects ?? []).map((r) => [r.project.organizationId, r.project.organization ?? r.project.organizationId])).entries()];

  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(changes)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-tinta">{t('ca.titulo')}</h1>
        <p className="max-w-2xl text-sm text-slate-600">{t('ca.descripcion')}</p>
      </div>
      <ProjectsNav />

      <div className="flex flex-wrap items-end gap-3">
        {organizations.length > 1 && (
          <div className="w-56">
            <Select id="ca-org" label={t('ca.organizacion')} value={organization ?? ''} onChange={(e) => update({ organizacion: e.target.value || null })}>
              <option value="">{t('ca.todas')}</option>
              {organizations.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </Select>
          </div>
        )}
        <div className="pb-2">
          <Checkbox label={t('ca.solo_estancados')} checked={staleOnly} onChange={(e) => update({ estancados: e.target.checked ? 'si' : null })} />
        </div>
      </div>

      {!board.data ? (
        board.error ? <Alert>{errorMessage(board.error)}</Alert> : <Spinner label={t('comun.cargando')} />
      ) : (
        <>
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-6" aria-label={t('ca.resumen')}>
            {SIDES.map((s) => (
              <div key={s} className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                <dt className="text-xs text-slate-500">{sideName(s)}</dt>
                <dd className="text-2xl font-semibold text-slate-900">{board.data.summary[s]}</dd>
              </div>
            ))}
            <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
              <dt className="text-xs text-slate-500">{t('ca.sin_movimiento', { dias: board.data.idleDays })}</dt>
              <dd className={`text-2xl font-semibold ${board.data.summary.stale > 0 ? 'text-red-700' : 'text-slate-900'}`}>{board.data.summary.stale}</dd>
            </div>
          </dl>

          <Card className="p-0">
            {board.data.projects.length === 0 ? (
              <p className="p-5 text-sm text-slate-500">{staleOnly ? t('ca.vacio_estancados') : t('ca.vacio')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-slate-200 text-xs text-slate-500">
                    <tr>
                      <th className="px-4 py-2 font-medium">{t('ca.col_proyecto')}</th>
                      <th className="px-4 py-2 font-medium">{t('ca.col_pelota')}</th>
                      <th className="px-4 py-2 font-medium">{t('ca.col_lado')}</th>
                      <th className="px-4 py-2 font-medium">{t('ca.col_movimiento')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {board.data.projects.map((r) => (
                      <tr key={r.project.id} className={r.stale ? 'bg-red-50/60' : 'hover:bg-slate-50'}>
                        <td className="px-4 py-2">
                          <Link to={`/proyectos/${r.project.number}`} className="font-medium text-slate-900 hover:underline">
                            <span className="mr-2 font-mono text-xs text-slate-500">{r.project.code}</span>
                            {r.project.name}
                          </Link>
                          <span className="block text-xs text-slate-500">{r.project.organization}</span>
                        </td>
                        <td className="px-4 py-2">
                          {r.current ? (
                            <>
                              <Link to={`/tareas/${r.current.id}`} className="text-slate-900 hover:underline">
                                {r.current.title}
                              </Link>
                              <span className="block text-xs text-slate-500">
                                {[r.current.phase, r.current.status, r.current.assignees.map((a) => a.fullName).join(', ') || t('ca.sin_asignados')].filter(Boolean).join(' · ')}
                              </span>
                            </>
                          ) : (
                            <span className="text-slate-400">{t('ca.nada_abierto')}</span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <SideBadge side={r.side} />
                        </td>
                        <td className={`px-4 py-2 ${r.stale ? 'font-medium text-red-700' : 'text-slate-600'}`}>
                          {r.stale && <span aria-hidden>⚠ </span>}
                          {t('ca.dias_sin_movimiento', { n: r.idleBusinessDays })}
                          <span className="block text-xs font-normal text-slate-500">{t('ca.ultimo', { fecha: formatDay(r.lastActivityAt) })}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

/** La Cancha de un proyecto, para su detalle. */
export function ProjectCanchaCard({ number }: { number: number }) {
  const board = useCancha({ proyecto: number });
  const row = board.data?.projects[0];
  if (!row) return null; // terminado, cancelado o en pausa: no está en la Cancha
  return (
    <Card title={t('ca.titulo_corto')}>
      <div className="space-y-1 text-sm">
        <p className="flex items-center gap-2">
          <SideBadge side={row.side} />
          {row.stale && <span className="font-medium text-red-700">{t('ca.alerta', { n: row.idleBusinessDays })}</span>}
        </p>
        {row.current ? (
          <p>
            {t('ca.pelota_en')}{' '}
            <Link to={`/tareas/${row.current.id}`} className="font-medium text-slate-900 hover:underline">
              {row.current.title}
            </Link>
            {row.current.assignees.length > 0 && <span className="text-slate-600"> ({row.current.assignees.map((a) => a.fullName).join(', ')})</span>}
          </p>
        ) : (
          <p className="text-slate-500">{t('ca.nada_abierto')}</p>
        )}
      </div>
    </Card>
  );
}
